import { useEffect } from "react";
import Header from './Header';
import Footer from './Footer';
import FAQ from './FAQ';
import { FaScroll, FaBan, FaCheckCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import Fuel_Saving from '../assets/Fuel_Station.png';
import { FaRecycle, FaGasPump, FaWallet, FaLeaf } from "react-icons/fa";

const Fuelstations = () => {
   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <>
         <Header />
         <div className="bg-[#f9fafb] text-gray-800">
            {/* Hero Section */}
            <div className="bg-[#f3f0eb]">
               <section className="relative flex flex-col md:flex-row items-center justify-between max-w-7xl mx-auto px-4 py-12">
                  <motion.div
                     initial={{ opacity: 0, x: -50 }}
                     animate={{ opacity: 1, x: 0 }}
                     transition={{ duration: 0.8 }}
                     className="md:w-1/2 space-y-6"
                  >

                     <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-[#1a4632]">
                        Turn Your <span className="text-[#9cc026]">Recycling Rewards</span> Into Fuel Savings
                     </h2>

                     <p className="text-lg text-gray-900">
                        At FareFuel, we believe recycling should do more than help the environment — it should benefit you too. That’s why we make it easy to convert your recycling rewards into fuel savings.
                     </p>

                     <div className="mt-8">
                        <Link
                           to="/register"
                           className="inline-block px-6 py-2 bg-gradient-to-r from-green-400 to-green-600 text-white text-lg font-semibold rounded-2xl shadow-lg hover:shadow-green-500/50 hover:scale-105 transition-all duration-300"
                        >
                           Get Started
                        </Link>
                        <p className="mt-2 text-sm text-gray-600 italic">
                           No hidden fees. Simple rewards. Eco-friendly impact.
                        </p>
                     </div>
                  </motion.div>

                  <motion.div
                     initial={{ opacity: 0, x: 50 }}
                     animate={{ opacity: 1, x: 0 }}
                     transition={{ duration: 0.8 }}
                     className="md:w-1/2 flex justify-center mt-10 md:mt-0"
                  >
                     <img
                        src={Fuel_Saving}
                        alt="Fuel Savings"
                        className="w-80 md:w-[400px] drop-shadow-xl"
                     />
                  </motion.div>
               </section>
            </div>

            {/* How It Works */}
            <section className="bg-white py-16">
               <div className="max-w-3xl mx-auto space-y-4">
                  <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1a4632] mb-12">
                     How Farefuel <span className="text-[#9cc026]">Works</span>
                  </h2>
                  <FAQ question={
                        <span className="flex items-center gap-2">
                           <FaScroll className="text-[#794f00]" /> Recycle & Earn
                        </span>
                     }
                     answer={
                        <p className="mb-2">
                           Drop off your eligible recyclables through the FareFuel program. Each item is scanned and rewards are credited to your account.
                        </p>
                     }
                  />
                  <FAQ question={
                        <span className="flex items-center gap-2">
                        <FaWallet className="text-[#b91c1c]" /> Track Your Balance
                        </span>
                     }
                     answer={
                        <p className="mb-2">
                           Log in anytime to view your rewards balance. Rewards accumulate automatically as you recycle more.
                        </p>
                     }
                  />

                  <FAQ question={
                        <span className="flex items-center gap-2">
                           <FaCheckCircle className="text-green-600" /> Redeem for Fuel
                        </span>
                     }
                     answer={
                        <p className="mb-2">
                           Visit any participating fuel partner. At checkout, choose “Pay with FareFuel” and apply your rewards to reduce your cost instantly.
                        </p>
                     }
                  />
               </div>
            </section>

            {/* Why Use FareFuel */}
            <section className="py-16 bg-green-50">
               <div className="max-w-6xl mx-auto px-6 text-center">
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1a4632] mb-12">
                     Why Use <span className="text-[#9cc026]">FareFuel?</span>
                  </h2>

                  <div className="grid md:grid-cols-3 gap-10">

                     <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="bg-[#ECFFDC] border border-[#25994c] rounded-lg p-6 shadow-sm transition duration-300 ease-in-out hover:shadow-[0_8px_30px_4px_rgba(116,175,32,0.4)]"
                     >
                        <FaRecycle className="text-green-700 text-5xl mx-auto mb-4" />
                        <h3 className="text-xl text-[#25994c] font-semibold mb-2">Earn Rewards</h3>
                        <p className="text-gray-600">
                           Every bottle, can, or recyclable item adds to your rewards and helps reduce your fuel costs.
                        </p>
                     </motion.div>

                     <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="bg-[#ECFFDC] border border-[#25994c] rounded-lg p-6 shadow-sm transition duration-300 ease-in-out hover:shadow-[0_8px_30px_4px_rgba(116,175,32,0.4)]"
                     >
                        <FaLeaf className="text-green-700 text-5xl mx-auto mb-4" />
                        <h3 className="text-xl text-[#25994c] font-semibold mb-2">Support Sustainability</h3>
                        <p className="text-gray-600">
                           Your everyday recycling contributes to cleaner cities and a greener future.
                        </p>
                     </motion.div>

                     <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="bg-[#ECFFDC] border border-[#25994c] rounded-lg p-6 shadow-sm transition duration-300 ease-in-out hover:shadow-[0_8px_30px_4px_rgba(116,175,32,0.4)]"
                     >
                        <FaGasPump className="text-green-700 text-5xl mx-auto mb-4" />
                        <h3 className="text-xl text-[#25994c] font-semibold mb-2">Simple & Convenient</h3>
                        <p className="text-gray-600">
                           No hassle — just scan, recycle, and use your rewards toward fuel.
                        </p>
                     </motion.div>

                  </div>
               </div>
            </section>

            {/* CTA Section */}
            <section className="py-12 bg-[#9cc026] text-center text-white">
               <motion.h2
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="text-3xl md:text-4xl font-bold"
               >
                  Recycle More. Fuel Smarter.
               </motion.h2>
            </section>
         </div>
         <Footer />
      </>
   );
};

export default Fuelstations;
